import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';

import type { Editor } from '@tiptap/react';
import type { Node as PmNode } from '@tiptap/pm/model';

import { StaleDocumentWriteError } from '@/hooks/rxdb-base';
import { getEditorHTML, isEqualHTML } from '@/utils/editor-utils';

interface UseSyncedEditorContentOptions {
  documentKey: string;
  editorContent: string;
  getLiveEditor: () => Editor | null;
  /** Live content while no editor is alive. Defaults to `editorContent`, which is usually right. */
  fallbackContent?: string | undefined;
  documentContent: string | undefined;
}

interface UseSyncedEditorContentResult {
  hasUserEdits: boolean;
  getLiveContent: () => string | undefined;
  markSynced: (html: string | undefined) => void;
}

/**
 * Tracks whether the live editor holds content the document has not been told about yet.
 *
 * The comparison runs on every render of an editor host, so it must not walk the document: a
 * keystroke would otherwise pay a full serialize-and-reparse of the whole note. It instead compares
 * the editor's current `state.doc` against an *anchor* — a doc known to carry the last synced
 * content. ProseMirror rebuilds only the touched path per transaction and `Node.eq` short-circuits
 * on reference identity, so an anchored comparison costs about one pointer check per top-level node
 * and stays flat as the note grows, while still reporting a doc edited back to the synced content
 * (a backspace, an undo) as clean.
 *
 * Anchors always come from the live editor, so its own docs are compared exactly. Content arriving
 * as stored HTML — the seed, an external write — is compared with `isEqualHTML` instead, which
 * normalizes the whitespace and `_hash` noise that survives a round trip through storage; a match
 * there anchors the live doc and hands later renders back to the fast path.
 *
 * Reading live content goes through `getLiveContent` so the hook keeps the doc each string was
 * serialized from: `markSynced` is handed content captured before an async write, and only that
 * pairing can tell edits typed during the write apart from the ones the write covered.
 */
export const useSyncedEditorContent = ({
  documentKey,
  editorContent,
  getLiveEditor,
  fallbackContent = editorContent,
  documentContent,
}: UseSyncedEditorContentOptions): UseSyncedEditorContentResult => {
  const lastSyncedContentRef = useRef<string | undefined>(editorContent);
  const syncedDocRef = useRef<PmNode | undefined>(undefined);
  const lastReadContentRef = useRef<{ html: string; doc: PmNode } | undefined>(undefined);
  const comparedContentRef = useRef<{ live?: string; baseline?: string; isSame: boolean } | undefined>(undefined);
  const baselinePinnedRef = useRef(false);

  const documentKeyRef = useRef(documentKey);
  if (documentKeyRef.current !== documentKey) {
    documentKeyRef.current = documentKey;
    lastSyncedContentRef.current = editorContent;
    syncedDocRef.current = undefined;
    lastReadContentRef.current = undefined;
    comparedContentRef.current = undefined;
    baselinePinnedRef.current = false;
  }

  const getLiveContent = () => {
    const editor = getLiveEditor();
    if (!editor) return fallbackContent;

    const html = getEditorHTML(editor);
    lastReadContentRef.current = { html, doc: editor.state.doc };
    return html;
  };

  const liveEditor = getLiveEditor();
  const liveDoc = liveEditor?.state.doc;

  // Hosts mount this hook before their editor exists, and some seed `editorContent` with a
  // placeholder until the document loads. Nothing can be an unsaved edit before an editor has held
  // the content, so the baseline follows `editorContent` until something establishes one — an
  // editor appearing, a sync, a self-heal. After that it only moves at sync points, or an external
  // write would silently swallow live edits.
  if (liveEditor) {
    baselinePinnedRef.current = true;
  } else if (!baselinePinnedRef.current && lastSyncedContentRef.current !== editorContent) {
    lastSyncedContentRef.current = editorContent;
    comparedContentRef.current = undefined;
  }

  // Node types belong to the schema that built them, so an anchor left behind by a different editor
  // instance could never match and has to be re-earned through the HTML comparison.
  const anchorDoc = liveEditor && syncedDocRef.current?.type.schema === liveEditor.schema ? syncedDocRef.current : undefined;

  let hasUserEdits: boolean;
  if (liveDoc && anchorDoc) {
    hasUserEdits = !liveDoc.eq(anchorDoc);
  } else {
    hasUserEdits = !isSameEditorContentFor(liveEditor, getLiveContent(), lastSyncedContentRef.current, comparedContentRef);
    if (!hasUserEdits && liveDoc) {
      syncedDocRef.current = liveDoc;
    }
  }

  const markSynced = useCallback(
    (html: string | undefined) => {
      // Writes resolve asynchronously, so this can fire for a document the host has already left.
      // Applying it anyway would overwrite the baseline the key change just seeded and strand the
      // new document reporting edits it does not have.
      if (documentKeyRef.current !== documentKey) return;

      lastSyncedContentRef.current = html;
      comparedContentRef.current = undefined;
      baselinePinnedRef.current = true;
      // Anchor on the doc `html` was serialized from rather than on whatever the editor holds now: a
      // save that lands while the user keeps typing must not swallow the edits made during the write.
      // An unrecognised string leaves the anchor unset; the next render re-earns one for free, since
      // unchanged content serializes back to the very same memoized string.
      const lastRead = lastReadContentRef.current;
      syncedDocRef.current = lastRead && lastRead.html === html ? lastRead.doc : undefined;
    },
    [documentKey]
  );

  useEffect(() => {
    if (documentContent === undefined) return;

    const liveContent = getLiveContent();
    if (!isSameEditorContentFor(getLiveEditor(), liveContent, documentContent, comparedContentRef)) return;

    lastSyncedContentRef.current = liveContent;
    comparedContentRef.current = undefined;
    baselinePinnedRef.current = true;
    syncedDocRef.current = getLiveEditor()?.state.doc;
  }, [documentContent]);

  return { hasUserEdits, getLiveContent, markSynced };
};

/**
 * Compares under the live editor's schema when there is one, so the verdict is reached with the same
 * node types as the anchor it earns. Memoized on the exact pair of strings compared: without an
 * anchor this runs on every render, and a dirty editor never earns one, so re-deciding an unchanged
 * pair would repay the full serialize-and-reparse the anchor exists to avoid.
 */
const isSameEditorContentFor = (
  editor: Editor | null,
  a: string | undefined,
  b: string | undefined,
  cache: MutableRefObject<{ live?: string; baseline?: string; isSame: boolean } | undefined>
) => {
  if (a === b) return true;

  const cached = cache.current;
  if (cached && cached.live === a && cached.baseline === b) return cached.isSame;

  const isSame = typeof a === 'string' && typeof b === 'string' ? isEqualHTML(a, b, editor) : Object.is(a, b);
  cache.current = { live: a, baseline: b, isSame };

  return isSame;
};

interface SyncedEditorContentWriteOptions {
  willWrite: boolean;
  liveContent: string | undefined;
  markSynced: (html: string | undefined) => void;
}

/**
 * Shared optimistic-concurrency write tail for editor autosave paths: marks the synced baseline
 * on success (when the write actually included live content), then swallows StaleDocumentWriteError
 * so a lost-update conflict doesn't surface as an autosave failure.
 */
export const withSyncedEditorContentWrite = <T>(
  write: Promise<T>,
  { willWrite, liveContent, markSynced }: SyncedEditorContentWriteOptions
): Promise<T | undefined> =>
  write
    .then((saved) => {
      if (willWrite) {
        markSynced(liveContent);
      }
      return saved;
    })
    .catch((error) => {
      if (error instanceof StaleDocumentWriteError) {
        return undefined;
      }
      throw error;
    });
